/* dialog-content.tsx generated by @compiled/babel-plugin v3.0.1 */
import "./dialog-content.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
import React, { forwardRef, useCallback, useId, useLayoutEffect, useRef } from 'react';
import { cx } from '@compiled/react';
import { bindAll } from 'bind-event-listener';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import noop from '@atlaskit/ds-lib/noop';
import { useNotifyOpenLayerObserver } from '@atlaskit/layering/use-notify-open-layer-observer';
import { useAnimatedVisibility } from '../internal/use-animated-visibility';
import { useFocusWrap } from '../internal/use-focus-wrap';
import { useSafariEscapeFix } from '../internal/use-safari-escape-fix';
// Surface reset — see the rationale on `surfaceResetStyles` in `popover/popover.tsx`.
// Neutralises inherited interaction and text-layout properties (e.g.
// `pointer-events: none` and `white-space: nowrap`) that leak into the top-layer
// surface. Excludes `color`/`font` (theming) and `direction`/`unicode-bidi` (RTL
// must inherit). The reset has no box side-effects, so it does not reintroduce
// the `margin: auto` centering problem that kept `height: auto` off `Dialog`.
//
// KEEP IN SYNC with the identical `surfaceResetStyles` in `popover/popover.tsx`
// (ADS forbids sharing styles across files, so it is co-located and duplicated).
// The `satisfies` check below fails the build if either copy drifts.
var surfaceResetStyles = {
  root: "_lcxv1wug _o5724jg8 _1nmz4jg8 _1i4q4jg8 _y3gnv2br _azhwidpf _1p1dglyw"
};
true;
var dialogStyles = {
  root: "_19itglyw _19pk1wug _2hwx1wug _otyr1wug _18u01wug _1q51ze3t _y4tize3t _85i5ze3t _bozgze3t _p12fglyw _c71lglyw _bfhk1j28",
  motion: "_k8m011k7 _1oeci9qs _1s9z1a5r _1o511hrf _bl8aglyw _156y147i",
  enter: "_y44v7kvq",
  exit: "_y44vgeqi"
};
var backdropStyles = {
  root: "_1ltk1i5c",
  hidden: "_1ltk1j28",
  enter: "_1m5exbuz",
  exit: "_1m5e1op4"
};

/**
 * Low-level `<dialog>` primitive. No visual opinions - no width, height,
 * background, border-radius, or layout. Consumers provide their own styling.
 *
 * Visibility is controlled declaratively via `isOpen`:
 * - `isOpen={true}` calls `.showModal()` (entry animation via `@starting-style`)
 * - `isOpen={false}` calls `.close()` (exit animation via `allow-discrete`)
 *
 * Handles native `cancel` event (Escape) and backdrop click detection.
 *
 * Close flow: allowed user dismissal closes the native dialog before `onClose`
 * notifies the consumer. The consumer must synchronize `isOpen` in response.
 *
 * Accessibility: render at least one focusable element (typically a close
 * button) when `isOpen` becomes `true`. Tab is always trapped inside the
 * dialog, so keyboard users need somewhere to land.
 */
export var Dialog = /*#__PURE__*/forwardRef(function Dialog(_ref, ref) {
  var children = _ref.children,
    isOpen = _ref.isOpen,
    onClose = _ref.onClose,
    _ref$dismissedBy = _ref.dismissedBy,
    dismissedBy = _ref$dismissedBy === void 0 ? 'escape-and-outside-click' : _ref$dismissedBy,
    onEnterFinish = _ref.onEnterFinish,
    onExitFinish = _ref.onExitFinish,
    _ref$shouldAnimate = _ref.shouldAnimate,
    shouldAnimate = _ref$shouldAnimate === void 0 ? false : _ref$shouldAnimate,
    consumerXcss = _ref.xcss,
    enteringAnimationXcss = _ref.enteringAnimationXcss,
    exitingAnimationXcss = _ref.exitingAnimationXcss,
    style = _ref.style,
    testId = _ref.testId,
    providedId = _ref.id,
    label = _ref.label,
    labelledBy = _ref.labelledBy,
    shouldHideBackdrop = _ref.shouldHideBackdrop;
  var generatedId = useId();
  var dialogId = providedId !== null && providedId !== void 0 ? providedId : generatedId;
  var ownRef = useRef(null);
  var combinedRef = mergeRefs([ownRef, ref]);
  var closeReasonRef = useRef(null);
  var dismissedByRef = useRef(dismissedBy);
  var onCloseRef = useRef(onClose);
  // Keep native event handlers stable while ensuring they read the latest committed props.
  useLayoutEffect(function () {
    dismissedByRef.current = dismissedBy;
    onCloseRef.current = onClose;
  }, [dismissedBy, onClose]);
  var _useAnimatedVisibilit = useAnimatedVisibility({
      isOpen: isOpen,
      shouldAnimate: shouldAnimate,
      elementRef: ownRef,
      onEnterFinish: onEnterFinish,
      onExitFinish: onExitFinish
    }),
    phase = _useAnimatedVisibilit.phase,
    isMounted = _useAnimatedVisibilit.isMounted,
    onBeforeToggle = _useAnimatedVisibilit.onBeforeToggle,
    onToggle = _useAnimatedVisibilit.onToggle;

  // Ordering is important: bind native lifecycle listeners before the later
  // layout effect calls showModal() or close(), because `beforetoggle` fires
  // synchronously during those calls.
  useLayoutEffect(function () {
    if (!isMounted) {
      return;
    }
    var dialog = ownRef.current;
    if (!dialog) {
      return;
    }
    return bindAll(dialog, [{
      type: 'beforetoggle',
      listener: onBeforeToggle
    }, {
      // Browsers retarget clicks on ::backdrop to the <dialog> element.
      // Close on backdrop click when outside-click dismissal is enabled.
      type: 'click',
      listener: function listener(event) {
        if (event.target === event.currentTarget && dismissedByRef.current === 'escape-and-outside-click') {
          closeReasonRef.current = 'overlay-click';
          dialog.close();
        }
      }
    }, {
      type: 'toggle',
      listener: function listener(event) {
        if (event.newState === 'closed') {
          var reason = closeReasonRef.current;
          closeReasonRef.current = null;
          // A reason is only set for user-initiated closes. When React closes the
          // dialog because isOpen changed, the consumer already knows.
          if (reason) {
            onCloseRef.current({
              reason: reason
            });
          }
        }
        onToggle(event);
      }
    }]);
  }, [isMounted, onBeforeToggle, onToggle]);

  // Native `<dialog>.showModal()` traps focus but wraps through `<body>` at
  // the boundary (A → B → C → body → A). This hook intercepts Tab to wrap
  // directly (A → B → C → A), matching the WAI-ARIA APG pattern. Passing
  // `phase` keeps the listener attached through the animated-exit window
  // (WCAG 2.4.3 Focus Order regression guard).
  useFocusWrap({
    elementRef: ownRef,
    role: 'dialog',
    phase: phase
  });

  // Notify the open layer observer so app-coordination features
  // (open-count subscriptions) work with top-layer dialogs.
  useNotifyOpenLayerObserver({
    type: 'modal',
    isOpen: isOpen,
    // No-op: no current use case for programmatic close via OpenLayerObserver.
    onClose: noop
  });
  useLayoutEffect(function () {
    var dialog = ownRef.current;
    if (!dialog) {
      return;
    }
    if (isOpen) {
      closeReasonRef.current = null;
      if (!dialog.open) {
        dialog.showModal();
      }
      return function () {
        if (dialog.open) {
          dialog.close();
        }
      };
    }
    if (dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // Safari bug: escape closes open dialog and popovers, rather than just innermost popover
  // See notes/decisions/safari-escape-nested-popover-in-dialog.md
  var _useSafariEscapeFix = useSafariEscapeFix({
      dialogRef: ownRef,
      isVisible: isMounted
    }),
    shouldIgnoreEscape = _useSafariEscapeFix.shouldIgnoreEscape;

  // Handle native Escape (cancel event)
  var handleCancel = useCallback(function (event) {
    // Only handle a cancel targeting THIS dialog. Native `cancel` does not
    // bubble, but React's synthetic event bubbles up the component tree, so
    // for nested dialogs (one rendered inside another's DOM subtree) the
    // inner dialog's Escape would otherwise also fire an ancestor dialog's
    // handler and close it too. Mirrors the backdrop-click target guard below.
    if (event.target !== event.currentTarget) {
      return;
    }

    // Spurious Safari `cancel`: the keydown snapshot saw an open child
    // popover, so this Escape belongs to that popover (light-dismissed
    // natively). Keep the dialog open instead of forwarding to `onClose`.
    if (shouldIgnoreEscape()) {
      event.preventDefault();
      return;
    }

    // Without native `closedby`, cancel Escape only when dismissal is disabled.
    // Allowed dismissal follows Popover behavior: the browser closes the element
    // and `onClose` notifies the controlled owner of the reason.
    if (dismissedBy === 'none') {
      event.preventDefault();
      return;
    }
    closeReasonRef.current = 'escape';
  }, [dismissedBy, shouldIgnoreEscape]);

  // Unmount the `<dialog>` once exit completes so it does not leave an
  // empty `role="dialog"` element in the accessibility tree. A later open
  // remounts a fresh element, so the listener-binding and native-visibility
  // effects run against that element.
  if (!isMounted) {
    return null;
  }
  var shouldAnimateBackdrop = !shouldHideBackdrop && shouldAnimate;
  return /*#__PURE__*/React.createElement("dialog", {
    ref: combinedRef,
    id: dialogId
    // `aria-modal` is intentionally NOT set: native `<dialog>.showModal()`
    // already conveys modal semantics to assistive tech, and double-
    // declaring it forecloses non-modal use cases (consumers calling
    // `.show()` would still appear modal). Modern AT (NVDA / JAWS /
    // VoiceOver) infer modality from the platform accessibility API.
    ,
    "aria-label": label,
    "aria-labelledby": label ? undefined : labelledBy,
    style: style,
    onCancel: handleCancel,
    "data-testid": testId
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/ui-styling-standard/local-cx-xcss, @compiled/local-cx-xcss
    ,
    className: ax([
    // Dialog styles
    dialogStyles.root, surfaceResetStyles.root, shouldAnimate && phase === 'entering' && dialogStyles.enter, shouldAnimate && phase === 'exiting' && dialogStyles.exit, shouldAnimate && dialogStyles.motion, shouldHideBackdrop ? backdropStyles.hidden : backdropStyles.root, shouldAnimateBackdrop && phase === 'entering' && backdropStyles.enter, shouldAnimateBackdrop && phase === 'exiting' && backdropStyles.exit, cx(consumerXcss, shouldAnimate && phase === 'entering' && enteringAnimationXcss, shouldAnimate && phase === 'exiting' && exitingAnimationXcss)])
  }, children);
});